// Considered: building payloads inline inside `StreamEntryMolecule`/`StreamPanelOrganism` —
// rejected. `ToolInspectorOrganism` (Phase 1) never branches on domain shape, only on `kind`; the
// translation from `StreamEntry`/`StreamChord` into a flat `InspectorPayload` belongs in one place
// so every entry point (row click, chip click, act button) produces an identical payload for the
// same fact. Pure functions — unit-testable without mounting the panel.
//
// Source: design/metaspace-tools/metaspace-tools-v2.jsx (CH_JOIN/CH_DELTA/CH_EXPLAIN/CH_VALIDATE/
// CH_IDENTITY/CH_THREAD payload literals) translated into the real `InspectorPayload` shape from
// `types/inspector.d.ts`.

import type { StreamChord, StreamEntry } from 'types/stream';
import type {
  ChordInspectorPayload,
  DerivationInspectorPayload,
  IdentityInspectorPayload,
  InspectorKeyValueRow,
  ProvenanceInspectorPayload,
  ThreadInspectorPayload,
  ValidationInspectorPayload,
  ValueHistoryInspectorPayload,
} from 'types/inspector';
import type { ConversationMessage } from '../../context/ToolbarContext';

export type StreamTranslateFn = (key: string, options?: Record<string, unknown>) => string;

function chordAddress(sphereId: string, seq: string): string {
  return `/sphere/${sphereId}/timeline?seq=${seq}`;
}

/** The "FACT" payload for a folded chord (e.g. a perspective-assignment join). */
export function buildChordPayload(
  chord: StreamChord,
  sphereId: string,
  title: string,
  t: StreamTranslateFn
): ChordInspectorPayload {
  const { root } = chord;
  const rows: InspectorKeyValueRow[] = [
    { label: t('toolInspector.rows.actor', { defaultValue: 'Actor' }), value: root.actorName },
    { label: t('toolInspector.rows.observedAt', { defaultValue: 'observedAt' }), value: root.observedAt, mono: true },
    { label: t('toolInspector.rows.recordedAt', { defaultValue: 'recordedAt' }), value: root.recordedAt, mono: true },
  ];

  return {
    kind: 'chord',
    icon: 'lucide:user-plus',
    title,
    subtitle: t('toolInspector.chord.subtitle', {
      defaultValue: 'One gesture, folded by correlationId.',
    }),
    address: chordAddress(sphereId, root.seq),
    rowsLabel: t('toolInspector.section.fact'),
    rows,
    forensics: [{ label: t('toolInspector.rows.correlationId', { defaultValue: 'correlationId' }), value: chord.correlationId, mono: true }],
    acts: [
      { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
      { id: 'open_sphere', label: t('stream.acts.openSphere'), variant: 'secondary', icon: 'lucide:arrow-up-right' },
    ],
    context: { sphereId, correlationId: chord.correlationId, seq: root.seq },
  };
}

/** The "THIS CHANGE" payload for an `aspect_observation` — DeltaRunAtom's click target. */
export function buildValueHistoryPayload(
  entry: StreamEntry,
  aspectLabel: string,
  sphereId: string,
  t: StreamTranslateFn
): ValueHistoryInspectorPayload {
  const propertyNames = Object.keys(entry.content ?? {});
  const priorEntries = entry.previous ? Object.entries(entry.previous) : [];
  const rows: InspectorKeyValueRow[] = [];

  if (priorEntries.length > 0) {
    rows.push({
      label: t('toolInspector.rows.prior', { defaultValue: 'Prior' }),
      value: priorEntries.map(([, v]) => String(v)).join(', '),
    });
  }
  rows.push(
    {
      label: t('toolInspector.rows.new', { defaultValue: 'New' }),
      value: propertyNames.map((k) => String(entry.content[k])).join(', '),
    },
    { label: t('toolInspector.rows.observedAt', { defaultValue: 'observedAt' }), value: entry.observedAt, mono: true },
    { label: t('toolInspector.rows.recordedAt', { defaultValue: 'recordedAt' }), value: entry.recordedAt, mono: true },
  );

  return {
    kind: 'value_history',
    icon: 'lucide:activity',
    title: `${aspectLabel} — ${t('toolInspector.kind.value_history')}`,
    subtitle: t('toolInspector.valueHistory.subtitle', {
      defaultValue: "Property-scoped history for this aspect's last changes.",
    }),
    address: `/sphere/${sphereId}/aspect/${entry.aspectSystemName}?seq=${entry.seq}`,
    rowsLabel: t('toolInspector.rowsLabel.thisChange', { defaultValue: 'THIS CHANGE' }),
    rows,
    // "Correct…" (README §1c / spec Phase 3 item 3): the inspector footer's inline input (the
    // act's `input` declaration) collects the corrected value; the panel's act handler appends
    // it as a correcting observation (`POST /spheres/:id/observations`) — history never rewritten.
    acts: [
      ...(propertyNames.length > 0 && entry.aspectSystemName
        ? [{
            id: 'correct' as const,
            label: t('toolInspector.acts.correct', { defaultValue: 'Correct…' }),
            variant: 'primary' as const,
            icon: 'lucide:pencil',
            confirmationLabel: t('toolInspector.acts.correctedConfirmation', {
              defaultValue: 'Correction appended — history preserved',
            }),
            input: {
              kind: (typeof entry.content[propertyNames[0]] === 'number' ? 'number' : 'text') as 'number' | 'text',
              placeholder: t('toolInspector.acts.correctPlaceholder', { defaultValue: 'Corrected value' }),
            },
          }]
        : []),
      { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
    ],
    note: t('toolInspector.valueHistory.note', {
      defaultValue: 'Correct… appends a correcting observation. History is never rewritten.',
    }),
    context: {
      sphereId,
      seq: entry.seq,
      aspectSystemName: entry.aspectSystemName ?? undefined,
      propertyName: propertyNames[0],
    },
  };
}

/** The `calculated`/`inferred` chip — DERIVATION section. */
export function buildDerivationPayload(
  entry: StreamEntry,
  aspectLabel: string,
  sphereId: string,
  t: StreamTranslateFn
): DerivationInspectorPayload {
  return {
    kind: 'derivation',
    icon: 'lucide:settings',
    title: `${aspectLabel} — ${t('stream.chips.calculated')}`,
    subtitle: t('toolInspector.derivation.subtitle', {
      defaultValue: 'Derived by the health reducer.',
    }),
    address: `/sphere/${sphereId}/aspect/${entry.aspectSystemName}/derivation`,
    acts: [
      { id: 'filter_to_calculated', label: t('toolInspector.acts.filterToCalculated', { defaultValue: 'Filter to calculated' }), variant: 'secondary', icon: 'lucide:filter' },
      { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
    ],
    context: { sphereId, seq: entry.seq, aspectSystemName: entry.aspectSystemName ?? undefined },
  };
}

/** The `claimed`/`imported` provenance chip — CLAIM section, no acts beyond a filter. */
export function buildProvenancePayload(
  entry: StreamEntry,
  aspectLabel: string,
  sphereId: string,
  t: StreamTranslateFn
): ProvenanceInspectorPayload {
  return {
    kind: 'provenance',
    icon: 'lucide:log-in',
    title: `${aspectLabel} — ${t('stream.chips.claimed')}`,
    subtitle: t('toolInspector.provenance.subtitle', {
      defaultValue: 'An import asserted this value; it has not been attested by a human.',
    }),
    address: `/sphere/${sphereId}/aspect/${entry.aspectSystemName}?seq=${entry.seq}`,
    rowsLabel: t('toolInspector.rowsLabel.claim', { defaultValue: 'CLAIM' }),
    rows: [{ label: t('toolInspector.rows.source', { defaultValue: 'Source' }), value: entry.sourceType ?? t('stream.chips.imported') }],
    acts: [{ id: 'filter_to_imported', label: t('toolInspector.acts.filterToImported', { defaultValue: 'Filter to imported' }), variant: 'secondary', icon: 'lucide:filter' }],
    context: { sphereId, seq: entry.seq, aspectSystemName: entry.aspectSystemName ?? undefined },
  };
}

/**
 * The `needs validation` chip / inline Attest·Reject buttons — CLAIM + EVIDENCE, with the real
 * Attest/Reject acts. Hidden entirely by the caller (never disabled) unless `viewerIsResponsible`.
 */
export function buildValidationPayload(
  entry: StreamEntry,
  aspectLabel: string,
  sphereId: string,
  t: StreamTranslateFn
): ValidationInspectorPayload {
  return {
    kind: 'validation',
    icon: 'lucide:shield-question',
    title: `${aspectLabel} — ${t('stream.chips.needsValidation')}`,
    subtitle: t('toolInspector.validation.subtitle', {
      defaultValue: 'Claimed by an import. Requires attestation before it counts as reported.',
    }),
    address: `/sphere/${sphereId}/validations/${entry.correlationId}`,
    rowsLabel: t('toolInspector.rowsLabel.claim', { defaultValue: 'CLAIM' }),
    rows: [
      { label: t('toolInspector.rows.value', { defaultValue: 'Value' }), value: Object.values(entry.content ?? {}).map(String).join(', '), mono: true },
      { label: t('toolInspector.rows.source', { defaultValue: 'Source' }), value: entry.sourceType ?? t('stream.chips.claimed') },
      // States what the SERVER enforces, not what this panel chooses to surface.
      // `POST /validations` gates on `assertAspectObservationAuthority` — sphere `edit`
      // plus the property's `access.write` — so any editor may attest. The panel narrows
      // the Attest/Reject acts to the Responsible on top of that (see
      // `viewerIsResponsible`), but the copy must not claim an exclusivity the server
      // does not enforce. Narrowing the server gate to Responsible is EAINT-859.
      { label: t('toolInspector.rows.whoMayAttest', { defaultValue: 'Who may attest' }), value: t('toolInspector.rows.whoMayAttestValue', { defaultValue: 'Anyone who may edit this sphere' }) },
    ],
    acts: [
      { id: 'attest', label: t('stream.acts.attest'), variant: 'primary', icon: 'lucide:check' },
      { id: 'reject', label: t('stream.acts.reject'), variant: 'secondary', icon: 'lucide:x' },
    ],
    context: { sphereId, seq: entry.seq, correlationId: entry.correlationId, aspectSystemName: entry.aspectSystemName ?? undefined },
  };
}

/** An actor name — PERSPECTIVES section. */
export function buildIdentityPayload(actorId: string, actorName: string, t: StreamTranslateFn): IdentityInspectorPayload {
  return {
    kind: 'identity',
    icon: 'lucide:user',
    title: actorName,
    address: `/identity/${actorId}`,
    acts: [{ id: 'open_identity_sphere', label: t('toolInspector.acts.openIdentitySphere', { defaultValue: 'Open identity sphere' }), variant: 'secondary', icon: 'lucide:arrow-up-right' }],
    context: { sphereId: actorId },
  };
}

/** The chat cluster header — THREAD (dated messages). */
export function buildThreadPayload(
  messages: ConversationMessage[],
  sphereId: string,
  threadId: string | undefined,
  t: StreamTranslateFn
): ThreadInspectorPayload {
  const lead = messages[0];
  return {
    kind: 'thread',
    icon: 'lucide:message-square',
    title: `${lead?.author_name ?? t('stream.unknownActor')} · ${t('toolInspector.thread.messageCount', { count: messages.length, defaultValue: `${messages.length} messages` })}`,
    address: `/sphere/${sphereId}/thread/${threadId ?? ''}`,
    rowsLabel: t('toolInspector.rowsLabel.thread', { defaultValue: 'THREAD' }),
    rows: messages.slice(-5).map((m) => ({ label: new Date(m.created_at).toLocaleDateString(), value: m.content })),
    // Reply uses the inspector footer's inline input (the act's `input` declaration); the panel's
    // act handler posts it through the same `useConversation.sendMessage` the chat cluster's own
    // composer uses — one thread, two entry points.
    acts: [
      {
        id: 'reply',
        label: t('toolInspector.acts.reply', { defaultValue: 'Reply…' }),
        variant: 'primary',
        icon: 'lucide:message-square',
        confirmationLabel: t('toolInspector.acts.replySentConfirmation', { defaultValue: 'Reply sent' }),
        input: {
          kind: 'text',
          multiline: true,
          placeholder: t('toolInspector.acts.replyPlaceholder', { defaultValue: 'Write a reply…' }),
        },
      },
      { id: 'copy_link', label: t('stream.acts.copyLink'), variant: 'secondary', icon: 'lucide:link' },
    ],
    context: { sphereId, threadId },
  };
}
